import { useMemo } from 'react';
import { apiCurrentUser } from 'api/currentUser';
import { apiSuppliers } from 'api/suppliers';
import { USE_FEDERATED_AUTH } from 'config';
import { useGet } from 'lib/api';
import type { Response } from 'types';

/** Normalized brand row the UI works with, independent of which API served it. */
export type AssociatedBrand = {
  /** Designer organization id — `id` on the Nexos route, `designerOrganizationId` on the legacy one. */
  id: number;
  name: string;
  /**
   * Optional org avatar colour. Neither API populates it today (no org-level colour exists in the
   * schema — see `AssociatedBrand.AvatarColor`, DL-61773); kept so the chip can use one if it lands.
   */
  avatarColor?: string;
};

/** Union of both wire shapes; every field optional so a partial row can't crash the map. */
type AssociatedBrandWire = {
  id?: number;
  designerOrganizationId?: number;
  name?: string;
  mercator?: string;
};

const toAssociatedBrands = (rows: AssociatedBrandWire[] | undefined): AssociatedBrand[] =>
  (rows ?? [])
    .map(row => ({
      id: row.id ?? row.designerOrganizationId ?? 0,
      name: row.name ?? '',
      avatarColor: row.mercator,
    }))
    .filter(brand => brand.id !== 0);

/**
 * The designer/brand organizations the signed-in supplier user is associated with.
 *
 * Picks the client for the active auth mode, the same way `useBrandsList` does: the Nexos
 * `GET /api/users/me/designer-organizations` under federated auth (the only mode where `delogueApi`
 * carries a bearer token), the legacy `GET /api/supplierportalaccount/associated-brands`
 * otherwise. Both run the same LoginId-scoped query, so the two lists are identical; only
 * the row shape differs, and `toAssociatedBrands` flattens it.
 */
export const useAssociatedBrands = (enabled: boolean = true) => {
  const url = USE_FEDERATED_AUTH
    ? apiCurrentUser.get.associatedOrganizations
    : apiSuppliers.get.associatedBrands;
  const key = USE_FEDERATED_AUTH
    ? 'current-user-associated-organizations'
    : 'guided-onboarding-associated-brands';

  const result = useGet<Response<AssociatedBrandWire[]>>(url, [key], [], undefined, true, {
    enabled,
  });

  const brands = useMemo(() => toAssociatedBrands(result.data?.data), [result.data]);

  return { ...result, brands };
};
